import { createClient } from '@supabase/supabase-js'

if (process.env.NODE_ENV === 'development') {
  console.log('🔥 SUPABASE INITIALIZATION STARTING')
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Log environment check in development only
if (process.env.NODE_ENV === 'development') {
  console.log('🔑 SUPABASE ENV CHECK:', {
    hasUrl: !!supabaseUrl,
    urlLength: supabaseUrl?.length,
    hasKey: !!supabaseAnonKey,
    keyLength: supabaseAnonKey?.length,
    env: import.meta.env.MODE,
    isDev: import.meta.env.DEV,
    isProd: import.meta.env.PROD
  })
}

if (!supabaseUrl || !supabaseAnonKey) {
  const error = new Error('⚠️ Missing Supabase environment variables')
  if (process.env.NODE_ENV === 'development') {
    console.error(error, {
      url: !!supabaseUrl,
      key: !!supabaseAnonKey
    })
  }
  throw error
}

// Create headers with API key
const headers = {
  'apikey': supabaseAnonKey,
  'Authorization': `Bearer ${supabaseAnonKey}`
}

if (process.env.NODE_ENV === 'development') {
  console.log('📦 Creating Supabase client...')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    storageKey: 'supabase.auth.token',
    debug: process.env.NODE_ENV === 'development',
    headers: headers,
    flowType: 'pkce',
    autoRefreshToken: true,
    pkce: {
      redirectTo: 'https://longtailwizard.com/auth/callback'
    },
    redirectTo: 'https://longtailwizard.com/auth/callback',
    cookieOptions: {
      domain: 'longtailwizard.com',
      name: 'sb-auth-token',
      secure: true,
      sameSite: 'lax',
      path: '/'
    }
  },
  global: {
    headers: headers
  },
  db: {
    schema: 'public'
  }
})

// Verify client creation in development only
if (process.env.NODE_ENV === 'development') {
  console.log('✅ SUPABASE CLIENT STATUS:', {
    hasClient: !!supabase,
    hasAuth: !!supabase?.auth,
    hasDB: !!supabase?.from
  })
}

// Debug session state in development only
supabase.auth.onAuthStateChange((event, session) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🔄 AUTH STATE CHANGE:', { 
      event,
      hasSession: !!session,
      hasUser: !!session?.user
    })
  }

  if (session?.access_token) {
    supabase.rest.headers = {
      ...headers,
      'Authorization': `Bearer ${session.access_token}`
    }
  }
})

// Initialize session check
supabase.auth.getSession().then(({ data: { session }, error }) => {
  if (error && process.env.NODE_ENV === 'development') {
    console.error('❌ Initial session error:', error.message)
  } else if (session && process.env.NODE_ENV === 'development') {
    console.log('✅ Initial session found')
  }
})

// Session refresh interval
if (typeof window !== 'undefined') {
  const refreshInterval = setInterval(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        supabase.rest.headers = {
          ...headers,
          'Authorization': `Bearer ${session.access_token}`
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Session refresh error:', error.message)
      }
    }
  }, 3600000) // 1 hour

  // Cleanup on window unload
  window.addEventListener('unload', () => clearInterval(refreshInterval))
}

export default supabase
